import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  deleteAllIncidents,
  deleteIncident,
  Incident,
  IncidentVerdict,
  setCollectionEnabled,
  useIncidentConfig,
  useIncidents,
} from '@/lib/api/incidents';
import { useFetchImageUrl } from '@/lib/hooks/useFetchImageUrl';

const VERDICT: Record<
  IncidentVerdict,
  { label: string; variant: 'destructive' | 'secondary' | 'outline' }
> = {
  killed: { label: 'Killed (process gone)', variant: 'destructive' },
  killed_lmk: { label: 'Killed (LMK/memory)', variant: 'destructive' },
  killed_manual: { label: 'Killed (manual/force-stop)', variant: 'secondary' },
  killed_sigkill: { label: 'Killed (SIGKILL/update)', variant: 'destructive' },
  crash: { label: 'Crash', variant: 'destructive' },
  display_sleep: { label: 'Display sleep', variant: 'secondary' },
  backgrounded: { label: 'Backgrounded', variant: 'secondary' },
  app_stuck: { label: 'App stuck', variant: 'secondary' },
  unknown: { label: 'Unknown', variant: 'outline' },
};

const PERIODS = [
  { label: 'Last 24 hours', value: '1' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'All', value: 'all' },
];

/** Each download link owns its own getDownloadURL query. */
function DownloadLink({ uri, label }: { uri?: string | null; label: string }) {
  const { data: url } = useFetchImageUrl(uri ?? '');
  if (!uri || !url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
    >
      <Download className="h-3 w-3" />
      {label}
    </a>
  );
}

function collectionLabel(s: Incident['collectionStatus']) {
  switch (s) {
    case 'collected':
      return null;
    case 'pending':
      return 'collecting…';
    case 'adb_unreachable':
      return 'ADB unreachable';
    case 'skipped_disabled':
      return 'collection off';
    default:
      return 'error';
  }
}

function toCsv(rows: Incident[]) {
  const cols = [
    'detectedAt',
    'name',
    'ip',
    'verdict',
    'collectionStatus',
    'pidAlive',
    'foreground',
    'wakefulness',
    'displayState',
    'memAvailableKb',
    'bootreason',
    'uptime',
    'model',
    'osVersion',
    'thermalMaxC',
    'dropboxHasCrash',
  ];
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) =>
    cols
      .map((c) =>
        c === 'detectedAt'
          ? esc(r.detectedAt ? r.detectedAt.toISOString() : '')
          : esc((r as unknown as Record<string, unknown>)[c])
      )
      .join(',')
  );
  return [cols.join(','), ...lines].join('\n');
}

export function Incidents() {
  const [incidents] = useIncidents();
  const [config] = useIncidentConfig();
  const [boxFilter, setBoxFilter] = useState('all');
  const [period, setPeriod] = useState('7');
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const handleDeleteOne = async (incident: Incident) => {
    try {
      await deleteIncident(incident.id);
      toast.success('Incident deleted');
    } catch (error) {
      console.error('Error deleting incident:', error);
      toast.error('Failed to delete incident');
    }
  };

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      const count = await deleteAllIncidents();
      toast.success(`${count} incident(s) deleted`);
      setDeleteAllOpen(false);
    } catch (error) {
      console.error('Error deleting incidents:', error);
      toast.error('Failed to delete incidents');
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Default ON when the field is absent.
  const enabled = config?.collectionEnabled !== false;

  const boxOptions = useMemo(() => {
    const map = new Map<string, string>();
    (incidents ?? []).forEach((i) => map.set(i.ip, i.name));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [incidents]);

  const filtered = useMemo(() => {
    const cutoff =
      period === 'all' ? 0 : Date.now() - Number(period) * 24 * 60 * 60 * 1000;
    return (incidents ?? []).filter((i) => {
      if (boxFilter !== 'all' && i.ip !== boxFilter) return false;
      if (cutoff && (!i.detectedAt || i.detectedAt.getTime() < cutoff))
        return false;
      return true;
    });
  }, [incidents, boxFilter, period]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incidents-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full pb-4">
      <h1 className="text-3xl font-bold">Incident Log</h1>
      <Separator className="my-6" />

      {/* Auto-collection toggle */}
      <div className="flex items-center justify-between rounded-md border p-4">
        <div>
          <p className="font-medium">Auto log collection</p>
          <p className="text-sm text-muted-foreground">
            When on, a command-fail discovery triggers an immediate ADB health
            snapshot (logcat + probes). Turn off during live events to avoid
            touching boxes — incidents are still logged, just without collection.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {enabled ? 'On' : 'Off'}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => setCollectionEnabled(v)}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Select value={boxFilter} onValueChange={setBoxFilter}>
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder="All boxes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All boxes</SelectItem>
            {boxOptions.map(([ip, name]) => (
              <SelectItem key={ip} value={ip}>
                {name} ({ip})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-muted-foreground">
          {filtered.length} incident{filtered.length === 1 ? '' : 's'}
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}>
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
        <Button
          variant="destructive"
          onClick={() => setDeleteAllOpen(true)}
          disabled={!incidents?.length}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Delete all
        </Button>
      </div>

      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Detected</TableHead>
              <TableHead>Box</TableHead>
              <TableHead>Verdict</TableHead>
              <TableHead>Key state</TableHead>
              <TableHead>Logs</TableHead>
              <TableHead className="w-px"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No incidents in this range.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((i) => {
                const note = collectionLabel(i.collectionStatus);
                const v = i.verdict ? VERDICT[i.verdict] : null;
                return (
                  <TableRow key={i.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {i.detectedAt
                        ? format(i.detectedAt, 'MMM d, HH:mm:ss')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{i.name}</div>
                      <div className="text-xs text-muted-foreground">{i.ip}</div>
                    </TableCell>
                    <TableCell>
                      {v ? (
                        <Badge variant={v.variant}>{v.label}</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {note ?? '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {i.collectionStatus === 'collected' ? (
                        <>
                          {i.foreground && <div>fg: {i.foreground}</div>}
                          {i.wakefulness && <div>wake: {i.wakefulness}</div>}
                          {i.memAvailableKb != null && (
                            <div>
                              free: {Math.round(i.memAvailableKb / 1024)} MB
                            </div>
                          )}
                          {i.bootreason && <div>boot: {i.bootreason}</div>}
                        </>
                      ) : (
                        note
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <DownloadLink uri={i.logcatUri} label="logcat" />
                        <DownloadLink uri={i.snapshotUri} label="snapshot" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteOne(i)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete all incidents</DialogTitle>
            <DialogDescription>
              This permanently deletes every record in the incident log —
              including ones outside the current filter — and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteAllOpen(false)}
              disabled={isDeletingAll}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={isDeletingAll}
            >
              {isDeletingAll ? 'Deleting...' : 'Delete all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Incidents;
